import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { Event, FileAsset, ImageAsset, ViewModelInstance } from '@rive-app/react-canvas';
import { Alignment, Fit, Layout } from '@rive-app/react-canvas';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/buttons/Button';
import type { IButtonProps } from '../../components/buttons/buttonTypes';
import { useVwToPx } from '../../components/EventTriggerDialog/hooks/useVwToPx';
import { EventActionType } from '../../components/EventTriggerDialog/RiveAnimation/types';
import { Loader } from '../../components/Loader/Loader';
import { useRiveMachine } from '../../hooks/rive/useRiveMachine';
import { useLocale } from '../../hooks/useLocale';
import { getUserLanguage } from '../../i18n/helpers';
import { useStores } from '../../stores/useStores';
import { isOfferV2PopupType } from '../../utils/events';
import { getImageUrlWithBucketPrefix } from '../../utils/images';
import { devError } from '../../utils/log';

import styles from './CategoryBannerCard.module.scss';
import { applyRiveAssetImage, isRiveAssetSlot, replaceRiveFileAsset, slotPaths } from './riveAssetSlots';
import type { CategoryBanner } from './types';

function ActionButton({
  element,
  parentRef,
  onClick,
}: {
  element: NonNullable<CategoryBanner['layout']>['elements']['ACTION_BTN'];
  parentRef: React.RefObject<HTMLDivElement>;
  onClick: () => void;
}) {
  const fontSize = useVwToPx({ vwValue: Number(element?.style?.fontSize || 2), ref: parentRef });
  if (!element) return null;
  const { filter: _filter, ...restStyle } = element.style ?? {};

  return (
    <div
      className={styles.actionButtonWrapper}
      style={{
        left: `${element.left}%`,
        top: `${element.top}%`,
        width: `${element.width}%`,
        height: `${element.height}%`,
      }}
      onClick={onClick}
    >
      <Button
        btnType={element.btnType as IButtonProps['btnType']}
        className={styles.actionButton}
        style={{ ...restStyle, fontSize }}
      >
        {element.text}
      </Button>
    </div>
  );
}

const CTA_DISPATCH_DEDUPE_MS = 250;
// Rive event name mobile's CategoryBannerTile listens for.
const RIVE_BUTTON_PRESSED_EVENT_NAME = 'ButtonUpPressed';

export const CategoryBannerCard = observer(function CategoryBannerCard({
  banner,
  onCtaClick,
}: {
  banner: CategoryBanner;
  onCtaClick: () => void;
}) {
  const { eventTriggerStore } = useStores();
  const { localeCurrencySymbol } = useLocale();
  const parentRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);
  const hasCta = banner.ctaType !== 'NONE';
  const handleCtaClickRef = useRef<() => void>(onCtaClick);
  const hasCtaRef = useRef(hasCta);
  hasCtaRef.current = hasCta;
  const lastCtaDispatchAtRef = useRef(0);

  // The player's own event-trigger packages win over the banner's catalog prices.
  const offerPackages = useMemo(() => {
    const catalogPackages = banner.offerPackages ?? [];
    if (banner.ctaType !== 'OFFER' || !banner.ctaValue) return catalogPackages;

    const data = eventTriggerStore.lastEventTriggerResponse;
    const event = [...(data?.currentEventsByTrigger ?? []), ...(data?.otherActiveEvents ?? [])].find(
      (triggerEvent) => (triggerEvent.benefit?.key ?? triggerEvent.benefitKey) === banner.ctaValue,
    );
    const { benefit } = event ?? {};
    if (!benefit || !isOfferV2PopupType(benefit) || !benefit.packages.length) return catalogPackages;

    return benefit.packages.map((pkg) => ({ gcCoin: pkg.gcCoin, scCoin: pkg.scCoin, price: pkg.price }));
  }, [banner.ctaType, banner.ctaValue, banner.offerPackages, eventTriggerStore.lastEventTriggerResponse]);
  // A single price only makes sense for a single package — several open a picker on tap.
  const offerPricing = offerPackages.length === 1 ? offerPackages[0] : banner.offerPricing;

  // Rive captures onStateChange at construction, so read the latest CTA state through refs.
  const onStateChange = useCallback((event: Event) => {
    if (!Array.isArray(event.data) || !hasCtaRef.current) return;
    if ((event.data as EventActionType[]).includes(EventActionType.ButtonUpPressed)) {
      handleCtaClickRef.current();
    }
  }, []);

  // Same CTA as a Rive event named ButtonUpPressed (what mobile listens for), so one file works on both.
  const onRiveEvent = useCallback((event: Event) => {
    if (!hasCtaRef.current) return;
    if ((event.data as { name?: string } | undefined)?.name === RIVE_BUTTON_PRESSED_EVENT_NAME) {
      handleCtaClickRef.current();
    }
  }, []);

  // Fill (not RIVE_DEFAULTS' Contain): the container is locked to the artboard's 1074/242 ratio, so
  // Fill never distorts and avoids sub-pixel letterbox gaps. Rive's Fit is separate from the CSS
  // object-fit on the image fallback.
  const layout = useMemo(() => new Layout({ fit: Fit.Fill, alignment: Alignment.Center }), []);

  // Image assets named bind_asset_{n} in the file's Assets panel take the xlsx URL for the player's language.
  const assetLoader = useCallback(
    (asset: FileAsset) => {
      const url = asset.isImage && isRiveAssetSlot(asset.name) ? banner.riveTextValues?.[asset.name] : undefined;
      if (!url) return false;
      replaceRiveFileAsset(asset as ImageAsset, url).catch((e) =>
        devError('Failed to load category banner Rive asset', asset.name, e),
      );
      return true;
    },
    [banner.riveTextValues],
  );

  const {
    rive,
    isLoading: isRiveLoading,
    RiveComponent,
    error,
    timeoutError,
    fireRiveViewModelTrigger,
    setRiveViewModelString,
  } = useRiveMachine({
    src: banner.riveFileUrl ? getImageUrlWithBucketPrefix(banner.riveFileUrl) : undefined,
    autoplay: true,
    autoBind: true,
    layout,
    assetLoader,
    onStateChange,
    onRiveEvent,
  });

  const handleCtaClick = () => {
    const now = Date.now();
    if (now - lastCtaDispatchAtRef.current < CTA_DISPATCH_DEDUPE_MS) return;
    lastCtaDispatchAtRef.current = now;

    // Optional in the file — only fire it when present, instead of logging a view-model miss per click.
    if (rive?.viewModelInstance?.trigger('user_click')) fireRiveViewModelTrigger('user_click');
    onCtaClick();
  };
  handleCtaClickRef.current = handleCtaClick;

  // bind_locale (or bind/locale): enum the file's state machine uses for per-language styling.
  useEffect(() => {
    if (!rive) return;
    slotPaths('bind_locale').forEach((path) => {
      const locale = rive.viewModelInstance?.enum(path);
      if (locale && (!locale.values?.length || locale.values.includes(language))) locale.value = language;
    });
  }, [rive, language]);

  // Files with a card per option fire user_click_option_{n} from their own tap listeners. Any card runs the same
  // CTA (1 package -> order summary, several -> the offer), deduped with a direct tap on the banner.
  useEffect(() => {
    if (!rive || !hasCta) return undefined;
    const onCardTap = () => handleCtaClickRef.current();
    const triggers: NonNullable<ReturnType<ViewModelInstance['trigger']>>[] = [];
    for (let option = 1; ; option += 1) {
      const trigger = rive.viewModelInstance?.trigger(`user_click_option_${option}`);
      if (!trigger) break;
      trigger.on(onCardTap);
      triggers.push(trigger);
    }
    // Single-button files may expose the CTA as a view-model trigger instead of a state or event.
    const buttonTrigger = rive.viewModelInstance?.trigger(EventActionType.ButtonUpPressed);
    if (buttonTrigger) {
      buttonTrigger.on(onCardTap);
      triggers.push(buttonTrigger);
    }
    return () => triggers.forEach((trigger) => trigger.off(onCardTap));
  }, [rive, hasCta]);

  useEffect(() => {
    if (!rive) return;
    const currencySymbol = localeCurrencySymbol() || '$';
    const formatPrice = (price: number) => (price > 0 ? `${currencySymbol}${price.toFixed(2)}` : '');
    // Files carry either the legacy fields or bind_option_* (and only as many options as they design for),
    // so skip absent paths instead of logging a view-model miss for each.
    const setIfPresent = (key: string, value: string) => {
      slotPaths(key).forEach((path) => {
        if (rive.viewModelInstance?.string(path)) setRiveViewModelString(path, value);
      });
    };

    if (offerPricing) {
      setIfPresent('cc_amount', offerPricing.gcCoin.toLocaleString());
      setIfPresent('sc_amount', offerPricing.scCoin.toLocaleString());
      setIfPresent('price', formatPrice(offerPricing.price));
    }
    // bind_option_{n}_* is 1-based, in the offer's package order.
    offerPackages.forEach((pkg, index) => {
      const prefix = `bind_option_${index + 1}`;
      setIfPresent(`${prefix}_gc`, pkg.gcCoin.toLocaleString());
      setIfPresent(`${prefix}_sc`, pkg.scCoin.toLocaleString());
      setIfPresent(`${prefix}_price`, formatPrice(pkg.price));
      setIfPresent(`${prefix}_cta_price`, formatPrice(pkg.price));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rive, JSON.stringify(offerPackages), offerPricing?.gcCoin, offerPricing?.scCoin, offerPricing?.price]);

  // Admin xlsx values, applied by path: bind_asset_{n} rows are image URLs, the rest are strings.
  useEffect(() => {
    if (!rive || !banner.riveTextValues) return undefined;

    let stale = false;
    Object.entries(banner.riveTextValues).forEach(([path, value]) => {
      if (!isRiveAssetSlot(path)) {
        // Legacy text_* keys are full paths already; bind_* keys may sit flat or nested.
        const present = slotPaths(path).filter((candidate) => rive.viewModelInstance?.string(candidate));
        (present.length ? present : [path]).forEach((candidate) => setRiveViewModelString(candidate, value));
        return;
      }
      if (!rive.viewModelInstance) return;
      applyRiveAssetImage(rive.viewModelInstance, path, value, () => stale).catch((e) =>
        devError('Failed to load category banner Rive asset', path, e),
      );
    });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rive, banner.riveTextValues]);

  const showRive = !!banner.riveFileUrl && !error && !timeoutError;
  const actionButton = banner.layout?.elements?.ACTION_BTN;

  return (
    <div ref={parentRef} className={styles.categoryBanner} data-banner-id={banner.id}>
      {showRive ? (
        <>
          {isRiveLoading && <Loader />}
          {RiveComponent && (
            // Only the Rive's own buttons are tappable: they fire ButtonUpPressed / user_click_option_{n} (above).
            <RiveComponent key={banner.id} className={styles.categoryBannerRive} />
          )}
        </>
      ) : (
        <>
          {/* No ACTION_BTN in the layout: the whole static card is the CTA, so it still works after a Rive failure. */}
          {hasCta && !actionButton ? (
            <button
              aria-label={t('common.open')}
              className={styles.categoryBannerImageButton}
              type="button"
              onClick={handleCtaClick}
            >
              <img
                alt=""
                className={classNames(styles.categoryBannerImage, styles.clickable)}
                src={getImageUrlWithBucketPrefix(banner.imageUrl)}
              />
            </button>
          ) : (
            <img alt="" className={styles.categoryBannerImage} src={getImageUrlWithBucketPrefix(banner.imageUrl)} />
          )}
          {hasCta && actionButton && (
            <ActionButton element={actionButton} parentRef={parentRef} onClick={handleCtaClick} />
          )}
        </>
      )}
    </div>
  );
});
